import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBrand, useProductName } from "@/context/BrandContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inboundGroupIdsOf, toggleInboundGroupId } from "@/lib/agentInboundGroups";
import AgentVoicemailInbox from "@/components/AgentVoicemailInbox";
import { Headset, SignOut } from "@phosphor-icons/react";

const SESSION_LABEL = {
  conference: "Conference Session",
  direct_phone: "Direct Phone",
};

export default function AgentSelectCampaign() {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const productName = useProductName();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [company, setCompany] = useState("");
  const [emptyReason, setEmptyReason] = useState(null);
  const [campaignId, setCampaignId] = useState("");
  const [inboundGroupIds, setInboundGroupIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const emptyCopy = {
    schedule: "No campaigns are inside their operating hours right now. Ask your administrator to check the campaign schedule, or try again later.",
    group: "No campaigns are assigned to your user group. Ask your administrator to add your group on the campaign.",
    filtered: "No campaigns are available for your group within the current schedule. Ask your administrator to check group assignment and operating hours.",
    none: "No eligible active campaigns right now. Ask your administrator to start a Predictive or Manual campaign.",
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/agent/campaigns");
      setCampaigns(data.campaigns || []);
      setCompany(data.company_name || "");
      setEmptyReason(data.empty_reason || null);
      // Prefer server Task-12 message when no campaigns are eligible
      if (!(data.campaigns || []).length && data.message) {
        setError(data.message);
      }
      if ((data.campaigns || []).length === 1) {
        const only = data.campaigns[0];
        setCampaignId(only.id);
        setInboundGroupIds(inboundGroupIdsOf(only.inbound_groups));
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        await logout();
        navigate("/agent/login", { replace: true });
        return;
      }
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => { load(); }, [load]);

  const selectedCamp = campaigns.find((c) => c.id === campaignId);

  const continueLogin = async () => {
    if (!campaignId) {
      setError("Select an active campaign to continue.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/agent/campaigns/select", {
        campaign_id: campaignId,
        inbound_group_ids: inboundGroupIds,
      });
      const stored = {
        ...(data.campaign || {}),
        inbound_groups: data.inbound_groups || data.campaign?.inbound_groups,
      };
      setSelected(stored);
      sessionStorage.setItem("wa_agent_campaign", JSON.stringify(stored));
      toast.success(`Campaign “${data.campaign.name}” selected`);
      // Conference Session and Direct Phone both continue into the Agent Workspace;
      // session init follows the campaign's configured agent_session type.
      if (data.campaign.agent_session === "conference" || data.campaign.agent_session === "direct_phone") {
        navigate("/agent/workspace", { replace: true });
        return;
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
      // Campaign may have gone inactive — refresh list
      load();
    } finally {
      setSaving(false);
    }
  };

  const signOut = async () => {
    await logout();
    sessionStorage.removeItem("wa_agent_campaign");
    navigate("/agent/login", { replace: true });
  };

  const logo = brand.logo_url || brand.login_logo_url;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="agent-select-campaign-page">
      <header className="border-b border-border px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {logo ? (
            <img src={logo} alt="" className="w-8 h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground grid place-items-center">
              <Headset className="w-4 h-4" weight="fill" />
            </div>
          )}
          <div className="min-w-0">
            <p className="font-display font-semibold text-sm truncate">{productName || "WACalls"}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Agent · Campaign</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground hidden sm:inline truncate max-w-[12rem]" data-testid="agent-select-user">
            {user?.name || user?.email}
          </span>
          <AgentVoicemailInbox variant="header" />
          <Button variant="ghost" size="sm" className="rounded-lg" onClick={signOut} data-testid="agent-logout-btn">
            <SignOut className="w-4 h-4 mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center p-6 sm:p-10">
        <div className="w-full max-w-md rounded-xl border border-border bg-card card-shadow p-6 sm:p-8">
          {!selected ? (
            <>
              <h1 className="text-xl font-display font-semibold tracking-tight">Select campaign</h1>
              <p className="text-sm text-muted-foreground mt-1.5">
                Choose an active campaign you are authorized to join
                {company ? ` for ${company}` : ""}.
              </p>

              <div className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label>Active campaign</Label>
                  {loading ? (
                    <p className="text-sm text-muted-foreground">Loading campaigns…</p>
                  ) : campaigns.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="agent-campaigns-empty">
                      {emptyCopy[emptyReason] || emptyCopy.none}
                    </p>
                  ) : (
                    <Select
                      value={campaignId}
                      onValueChange={(id) => {
                        setCampaignId(id);
                        const camp = campaigns.find((c) => c.id === id);
                        setInboundGroupIds(inboundGroupIdsOf(camp?.inbound_groups));
                      }}
                    >
                      <SelectTrigger data-testid="agent-campaign-select" className="h-11">
                        <SelectValue placeholder="Select a campaign" />
                      </SelectTrigger>
                      <SelectContent>
                        {campaigns.map((c) => (
                          <SelectItem key={c.id} value={c.id} data-testid={`agent-campaign-option-${c.id}`}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {selectedCamp && (
                  <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs space-y-1" data-testid="agent-campaign-preview">
                    <p><span className="text-muted-foreground">Dialing:</span> {(selectedCamp.dialing_type || "").replace(/_/g, " ")}</p>
                    <p><span className="text-muted-foreground">Type:</span> {selectedCamp.campaign_type}</p>
                    <p><span className="text-muted-foreground">Agent Session:</span> {SESSION_LABEL[selectedCamp.agent_session] || selectedCamp.agent_session}</p>
                  </div>
                )}

                {selectedCamp && (selectedCamp.inbound_groups || []).length > 0 && (
                  <div className="space-y-2" data-testid="agent-inbound-groups">
                    <Label>Inbound groups</Label>
                    <p className="text-xs text-muted-foreground">
                      Select one or more inbound groups for this session.
                    </p>
                    <div className="rounded-lg border border-border p-3 space-y-2">
                      {selectedCamp.inbound_groups.map((g) => (
                        <label
                          key={g.id}
                          className="flex items-center gap-2 text-sm"
                          data-testid={`agent-inbound-group-row-${g.id}`}
                        >
                          <Checkbox
                            checked={inboundGroupIds.includes(g.id)}
                            onCheckedChange={(on) => {
                              setInboundGroupIds((prev) => toggleInboundGroupId(prev, g.id, on === true));
                            }}
                            data-testid={`agent-inbound-group-${g.id}`}
                          />
                          <span className="truncate">{g.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {error && (
                  <p className="text-sm text-destructive" data-testid="agent-campaign-error">{error}</p>
                )}

                <Button
                  className="w-full h-11 rounded-lg"
                  disabled={saving || loading || !campaignId}
                  onClick={continueLogin}
                  data-testid="agent-campaign-continue"
                >
                  {saving ? "Validating…" : "Continue"}
                </Button>
              </div>
            </>
          ) : (
            <div data-testid="agent-campaign-selected">
              <h1 className="text-xl font-display font-semibold tracking-tight">Campaign selected</h1>
              <p className="text-sm text-muted-foreground mt-1.5">
                {selected.agent_session === "direct_phone"
                  ? "Continue to open the Direct Phone workspace — your phone is dialed when a customer is assigned."
                  : "Continue to start the Conference Session."}
              </p>
              <div className="mt-6 rounded-lg border border-border p-4 text-sm space-y-2">
                <p><span className="text-muted-foreground">Campaign:</span> <strong>{selected.name}</strong></p>
                <p><span className="text-muted-foreground">ID:</span> <span className="font-mono text-xs">{selected.id}</span></p>
                <p><span className="text-muted-foreground">Dialing type:</span> {(selected.dialing_type || "").replace(/_/g, " ")}</p>
                <p><span className="text-muted-foreground">Campaign type:</span> {selected.campaign_type}</p>
                <p><span className="text-muted-foreground">Agent Session:</span> {SESSION_LABEL[selected.agent_session] || selected.agent_session}</p>
                <p><span className="text-muted-foreground">Company:</span> {selected.company_name || company || "—"}</p>
                <p><span className="text-muted-foreground">Status:</span> {selected.status}</p>
              </div>
              <div className="mt-6 flex gap-2">
                {(selected.agent_session === "conference" || selected.agent_session === "direct_phone") && (
                  <Button
                    className="rounded-lg flex-1"
                    onClick={() => navigate("/agent/workspace", { replace: true })}
                    data-testid="agent-go-workspace"
                  >
                    Start session
                  </Button>
                )}
                <Button variant="outline" className="rounded-lg flex-1" onClick={() => { setSelected(null); load(); }} data-testid="agent-campaign-change">
                  Change campaign
                </Button>
                <Button variant="outline" className="rounded-lg" onClick={signOut}>Logout</Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
